import React, { useState, useEffect } from 'react';
import { 
  X, 
  Mail, 
  Lock, 
  Key, 
  ShieldAlert, 
  ArrowRight, 
  CheckCircle2, 
  Copy,
  Check,
  ExternalLink,
  Info,
  UserPlus,
  Send,
  Loader2
} from 'lucide-react';
import { 
  auth, 
  db, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  sendPasswordResetEmail,
  secureSetDoc, 
  secureGetDoc, 
  doc 
} from '../firebase';
import firebaseConfig from '../../firebase-applet-config.json';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess?: (userInfo: any) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onAuthSuccess
}) => {
  const [mode, setMode] = useState<'login' | 'register' | 'pin-setup' | 'forgot-password' | 'forgot-pin'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [directResetLink, setDirectResetLink] = useState('');
  const [isDomainError, setIsDomainError] = useState(false);
  const [isUserNotFound, setIsUserNotFound] = useState(false);
  const [domainCopied, setDomainCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userCredential, setUserCredential] = useState<any>(null);
  const [generatedId, setGeneratedId] = useState('');

  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const firebaseProjectId = firebaseConfig.projectId || 'project-c1721bd5-9afb-4859-927';
  const consoleAuthSettingsUrl = `https://console.firebase.google.com/project/${firebaseProjectId}/authentication/settings`;

  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccessMessage('');
      setDirectResetLink('');
      setIsDomainError(false);
      setIsUserNotFound(false);
      setDomainCopied(false);
      setEmail('');
      setPassword('');
      setPin('');
      setConfirmPin('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopyDomain = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(currentHostname);
      setDomainCopied(true);
      setTimeout(() => setDomainCopied(false), 2500);
    }
  };

  const parseAuthError = (err: any) => {
    const code = err?.code || '';
    const msg = err?.message || '';

    if (code === 'auth/unauthorized-domain' || msg.includes('auth/unauthorized-domain')) {
      return {
        message: 'โดเมนปัจจุบันยังไม่ได้เพิ่มใน Authorized Domains ของ Firebase Authentication',
        isDomain: true,
        isNotFound: false
      };
    }
    if (code === 'auth/invalid-credential' || code === 'auth/user-not-found' || msg.includes('auth/invalid-credential') || msg.includes('auth/user-not-found')) {
      return {
        message: 'ไม่พบบัญชีผู้ใช้นี้ หรือรหัสผ่านไม่ถูกต้อง',
        isDomain: false,
        isNotFound: true
      };
    }
    if (code === 'auth/wrong-password') {
      return {
        message: 'รหัสผ่านไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง',
        isDomain: false,
        isNotFound: false
      };
    }
    if (code === 'auth/email-already-in-use') {
      return {
        message: 'อีเมลนี้ถูกลงทะเบียนไว้แล้ว กรุณาเข้าสู่ระบบด้วยรหัสผ่านของคุณ',
        isDomain: false,
        isNotFound: false
      };
    }
    if (code === 'auth/weak-password') {
      return {
        message: 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษรขึ้นไป',
        isDomain: false,
        isNotFound: false
      };
    }
    if (code === 'auth/invalid-email') {
      return {
        message: 'รูปแบบอีเมลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง',
        isDomain: false,
        isNotFound: false
      };
    }
    if (code === 'auth/popup-closed-by-user') {
      return {
        message: 'หน้าต่างเข้าสู่ระบบด้วย Google ถูกปิดก่อนทำรายการสำเร็จ',
        isDomain: false,
        isNotFound: false
      };
    }
    if (code === 'auth/popup-blocked') {
      return {
        message: 'เบราว์เซอร์บล็อกหน้าต่างป๊อปอัป กรุณาอนุญาตป๊อปอัปสำหรับเว็บไซต์นี้',
        isDomain: false,
        isNotFound: false
      };
    }
    return {
      message: msg || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง',
      isDomain: false,
      isNotFound: false
    };
  };

  const handleGoogleSSO = async () => {
    try {
      setLoading(true);
      setError('');
      setIsDomainError(false);
      setIsUserNotFound(false);

      const provider = new GoogleAuthProvider();
      const res = await signInWithPopup(auth, provider);
      
      if (res.user) {
        // Check if user already exists in Firestore
        const userDocRef = doc(db, 'users', res.user.uid);
        const userSnap = await secureGetDoc(userDocRef, `users/${res.user.uid}`);
        
        if (userSnap && userSnap.exists()) {
          const userData = userSnap.data() as any;
          if (userData.isPinSetup) {
            onClose();
            if (onAuthSuccess) onAuthSuccess(userData);
          } else {
            setUserCredential(res.user);
            setMode('pin-setup');
          }
        } else {
          setUserCredential(res.user);
          setMode('pin-setup');
        }
      }
    } catch (err: any) {
      const parsed = parseAuthError(err);
      setError(parsed.message);
      setIsDomainError(parsed.isDomain);
      setIsUserNotFound(parsed.isNotFound);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setIsDomainError(false);
    setIsUserNotFound(false);
    setLoading(true);

    if (!email.trim()) {
      setError('กรุณาระบุอีเมลผู้ใช้งาน');
      setLoading(false);
      return;
    }
    if (!password || password.length < 6) {
      setError('รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร');
      setLoading(false);
      return;
    }

    try {
      if (mode === 'login') {
        const res = await signInWithEmailAndPassword(auth, email.trim(), password);
        if (res.user) {
          // Check if PIN is setup
          const userDocRef = doc(db, 'users', res.user.uid);
          const userSnap = await secureGetDoc(userDocRef, `users/${res.user.uid}`);
          if (userSnap && userSnap.exists()) {
            const userData = userSnap.data() as any;
            if (userData.isPinSetup) {
              onClose();
              if (onAuthSuccess) onAuthSuccess(userData);
            } else {
              setUserCredential(res.user);
              setMode('pin-setup');
            }
          } else {
            setUserCredential(res.user);
            setMode('pin-setup');
          }
        }
      } else {
        const res = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (res.user) {
          setUserCredential(res.user);
          setMode('pin-setup');
        }
      }
    } catch (err: any) {
      const parsed = parseAuthError(err);
      setError(parsed.message);
      setIsDomainError(parsed.isDomain);
      setIsUserNotFound(parsed.isNotFound);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRegisterFromLogin = async () => {
    if (!email.trim() || !password) return;
    setMode('register');
    setError('');
    setIsUserNotFound(false);
    setLoading(true);
    try {
      const res = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (res.user) {
        setUserCredential(res.user);
        setMode('pin-setup');
      }
    } catch (err: any) {
      const parsed = parseAuthError(err);
      setError(parsed.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setDirectResetLink('');
    
    if (!email.trim() || !email.includes('@')) {
      setError('กรุณากรอกอีเมลที่ลงทะเบียนให้ถูกต้อง');
      return;
    }

    setLoading(true);
    try {
      // 1. Firebase Auth standard password reset email
      try {
        await sendPasswordResetEmail(auth, email.trim());
      } catch (fbErr) {
        console.warn('Firebase sendPasswordResetEmail note:', fbErr);
      }

      // 2. Server email trigger
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await res.json();
      if (res.ok && data.success && data.resetLink) {
        setDirectResetLink(data.resetLink);
      }

      setSuccessMessage(`ระบบความปลอดภัยได้ส่งลิงก์สำหรับตั้งค่ารหัสผ่านใหม่ไปยังอีเมล ${email.trim()} เรียบร้อยแล้ว กรุณาตรวจสอบกล่องจดหมาย (Inbox/Spam)`);
    } catch (err: any) {
      setError(err?.message || 'ไม่สามารถส่งลิงก์รีเซ็ตรหัสผ่านได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setDirectResetLink('');

    if (!email.trim() || !email.includes('@')) {
      setError('กรุณากรอกอีเมลที่ลงทะเบียนให้ถูกต้อง');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/request-pin-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          origin: window.location.origin
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'เกิดข้อผิดพลาดในการสร้างลิงก์ตั้งค่า PIN');
      }

      setSuccessMessage(`ระบบได้ส่งลิงก์ความปลอดภัยสำหรับตั้งค่ารหัส PIN 6 หลักใหม่ไปยังอีเมล ${email.trim()} เรียบร้อยแล้ว (ลิงก์มีอายุ 15 นาที)`);
      if (data.resetLink) {
        setDirectResetLink(data.resetLink);
      }
    } catch (err: any) {
      setError(err?.message || 'ไม่สามารถส่งลิงก์ตั้งค่า PIN ได้');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (pin.length !== 6 || !/^\d+$/.test(pin)) {
      setError('รหัส PIN ต้องประกอบด้วยตัวเลข 6 หลักเท่านั้น');
      return;
    }

    if (pin !== confirmPin) {
      setError('รหัส PIN ไม่ตรงกัน กรุณาตรวจสอบอีกครั้ง');
      return;
    }

    setLoading(true);
    try {
      // Auto generate a unique 8-digit Customer ID
      const customerIdValue = Math.floor(10000000 + Math.random() * 90000000).toString();
      setGeneratedId(customerIdValue);

      const userId = userCredential.uid;
      const emailValue = (userCredential.email || email).trim();
      const isAdminEmail = (
        emailValue.toLowerCase() === 'giffarine108037654@gmail.com' ||
        emailValue.toLowerCase() === 'dlgiffarine@gmail.com' || 
        emailValue.toLowerCase() === 'admin@pinkgiffarine.com'
      );

      const newUserDoc = {
        id: userId,
        email: emailValue,
        customerId: customerIdValue,
        pinHash: pin,
        isPinSetup: true,
        role: isAdminEmail ? 'admin' : 'customer',
        createdAt: new Date().toISOString()
      };

      // Save user to Firestore
      await secureSetDoc(doc(db, 'users', userId), newUserDoc, `users/${userId}`);

      // Create initial profile in profiles collection
      const initialProfile = {
        userId,
        fullName: userCredential.displayName || (isAdminEmail ? 'เจ้าของระบบ & นักพัฒนา (Owner/Admin)' : 'ลูกค้ากิฟฟารีน'),
        phoneNumber: '',
        shippingAddress: '',
        updatedAt: new Date().toISOString()
      };
      await secureSetDoc(doc(db, 'users', userId, 'private', 'profile'), initialProfile, `users/${userId}/private/profile`);

      setLoading(false);
      onClose();
      if (onAuthSuccess) onAuthSuccess(newUserDoc);
    } catch (err: any) {
      setError(err?.message || 'การตั้งค่า PIN ล้มเหลว');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-pink-200/50 p-6 sm:p-8 flex flex-col text-slate-800 my-8">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Titles */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-100 text-[#2A59FE] mb-3 shadow-2xs">
            {mode === 'pin-setup' || mode === 'forgot-pin' ? (
              <Key className="w-6 h-6" />
            ) : mode === 'forgot-password' ? (
              <Lock className="w-6 h-6" />
            ) : (
              <Mail className="w-6 h-6" />
            )}
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-[#0E2C58]">
            {mode === 'login' && 'เข้าสู่ระบบสมาชิก'}
            {mode === 'register' && 'สมัครสมาชิกกิฟฟารีน'}
            {mode === 'pin-setup' && 'ตั้งค่ารหัส PIN ความปลอดภัย'}
            {mode === 'forgot-password' && 'ขอลิงก์ตั้งค่ารหัสผ่านใหม่'}
            {mode === 'forgot-pin' && 'ขอลิงก์ตั้งค่ารหัส PIN ใหม่'}
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {mode === 'login' && 'เข้าใช้บริการเพื่อสะสมแต้ม PV และการสั่งซื้อที่รวดเร็ว'}
            {mode === 'register' && 'สร้างบัญชีเพื่อสะสมแต้ม PV แลกของรางวัล และซื้อสินค้าราคาสมาชิก'}
            {mode === 'pin-setup' && 'กรุณาตั้งรหัส PIN 6 หลักเพื่อใช้ยืนยันการสั่งซื้อและการทำธุรกรรม'}
            {mode === 'forgot-password' && 'ระบุอีเมลที่ลงทะเบียนไว้ ระบบจะส่งลิงก์ตั้งรหัสผ่านใหม่ไปยังอีเมลของคุณ'}
            {mode === 'forgot-pin' && 'ระบุอีเมลที่ลงทะเบียนไว้ ระบบจะส่งลิงก์ตั้งรหัส PIN 6 หลักใหม่ไปยังอีเมลของคุณ'}
          </p>
        </div>

        {/* Domain Error Notice (auth/unauthorized-domain) */}
        {isDomainError ? (
          <div className="mb-5 p-4 bg-amber-50/90 border border-amber-200 rounded-2xl text-slate-800 text-xs shadow-xs space-y-2.5">
            <div className="flex items-start gap-2 text-amber-800 font-bold">
              <ShieldAlert className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
              <span>ความปลอดภัย Firebase: โดเมนยังไม่ได้รับอนุญาต (Authorized Domain)</span>
            </div>
            <p className="text-slate-600 leading-relaxed">
              Google SSO ตรวจพบว่าโดเมนปัจจุบันยังไม่ได้เพิ่มในรายการโดเมนที่ได้รับอนุญาตของ Firebase Authentication
            </p>
            
            <div className="bg-white p-2.5 rounded-xl border border-amber-200 flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-slate-700 truncate select-all">
                {currentHostname}
              </span>
              <button
                type="button"
                onClick={handleCopyDomain}
                className="shrink-0 px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg font-semibold text-[11px] flex items-center gap-1 transition-colors cursor-pointer"
              >
                {domainCopied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span>คัดลอกแล้ว</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>คัดลอกโดเมน</span>
                  </>
                )}
              </button>
            </div>

            <div className="pt-1 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px]">
              <a
                href={consoleAuthSettingsUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[#DB2777] font-bold hover:underline inline-flex items-center gap-1"
              >
                <span>เปิด Firebase Console เพื่อเพิ่มโดเมน</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <div className="pt-2 border-t border-amber-200/80">
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 text-[11px] font-medium flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>คุณสามารถใช้อีเมลและรหัสผ่านเข้าสู่ระบบด้านล่างได้ทันที โดยไม่ต้องรอเพิ่มโดเมน</span>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl flex flex-col gap-2 text-rose-700 text-xs font-semibold shadow-2xs">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
            {isUserNotFound && mode === 'login' && (
              <button
                type="button"
                onClick={handleQuickRegisterFromLogin}
                className="mt-1 py-1.5 px-3 bg-[#DB2777] hover:bg-[#BE185D] text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer w-full"
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>สมัครสมาชิกใหม่ด้วยอีเมลนี้ทันที</span>
              </button>
            )}
          </div>
        ) : null}

        {/* Success Message Banner */}
        {successMessage && (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs text-emerald-900 space-y-2">
            <div className="flex items-start gap-2 font-semibold">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
              <span>{successMessage}</span>
            </div>
            {directResetLink && (
              <a
                href={directResetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#DB2777] hover:underline bg-white px-3 py-2 rounded-xl border border-pink-200 shadow-2xs w-full justify-center mt-1"
              >
                <span>{mode === 'forgot-password' ? 'คลิกเพื่อตั้งค่ารหัสผ่านใหม่ทันที' : 'คลิกเพื่อเปิดหน้าตั้งค่า PIN ทันที'}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        )}

        {/* 1. Forgot Password Form */}
        {mode === 'forgot-password' ? (
          <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                อีเมลที่ลงทะเบียนไว้ <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-pink-200/60 focus:border-pink-500 focus:outline-none text-sm bg-pink-50/10 transition-colors"
                />
                <Mail className="absolute left-3.5 top-3 w-4 h-4 text-[#DB2777]" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white rounded-xl font-bold text-sm shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-2 active:scale-98 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังส่งลิงก์ไปยังอีเมล...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>ส่งลิงก์ตั้งค่ารหัสผ่านใหม่</span>
                </>
              )}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-xs text-slate-500 hover:text-[#DB2777] font-semibold cursor-pointer"
              >
                ← กลับสู่หน้าเข้าสู่ระบบ
              </button>
            </div>
          </form>
        ) : mode === 'forgot-pin' ? (
          /* 2. Forgot PIN Form */
          <form onSubmit={handleForgotPinSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                อีเมลที่ลงทะเบียนไว้ <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-pink-200/60 focus:border-pink-500 focus:outline-none text-sm bg-pink-50/10 transition-colors"
                />
                <Mail className="absolute left-3.5 top-3 w-4 h-4 text-[#DB2777]" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-[#FF6B97] via-[#F43F85] to-[#DB2777] text-white rounded-xl font-bold text-sm shadow-md hover:from-pink-600 hover:to-pink-800 transition-all flex items-center justify-center gap-2 active:scale-98 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังส่งลิงก์ความปลอดภัย...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>ส่งลิงก์ตั้งค่ารหัส PIN ใหม่</span>
                </>
              )}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-xs text-slate-500 hover:text-[#DB2777] font-semibold cursor-pointer"
              >
                ← กลับสู่หน้าเข้าสู่ระบบ
              </button>
            </div>
          </form>
        ) : mode === 'pin-setup' ? (
          /* 3. PIN setup screen */
          <form onSubmit={handlePinSetupSubmit} className="space-y-4">
            <div className="p-3 bg-pink-50 border border-pink-200 rounded-xl text-xs text-pink-900 mb-2">
              <span className="font-bold">ระบบความปลอดภัยสมาชิก:</span> รหัส PIN 6 หลักจะใช้ในการยืนยันตัวตนเมื่อทำการชำระเงินหรือสั่งซื้อสินค้า
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 text-center">
                ตั้งรหัส PIN 6 หลัก <span className="text-rose-500">*</span>
              </label>
              <input
                type="password"
                required
                maxLength={6}
                pattern="\d{6}"
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                className="w-full text-center tracking-widest text-xl py-3 rounded-xl border border-pink-200/60 focus:border-pink-500 focus:outline-none bg-pink-50/10 font-mono font-bold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 text-center">
                ยืนยันรหัส PIN 6 หลักอีกครั้ง <span className="text-rose-500">*</span>
              </label>
              <input
                type="password"
                required
                maxLength={6}
                pattern="\d{6}"
                placeholder="••••••"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                className="w-full text-center tracking-widest text-xl py-3 rounded-xl border border-pink-200/60 focus:border-pink-500 focus:outline-none bg-pink-50/10 font-mono font-bold"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-bold text-sm shadow-md hover:from-emerald-600 hover:to-teal-700 transition-all flex items-center justify-center gap-2 active:scale-98 disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <span>กำลังบันทึก...</span>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>ยืนยันการตั้งค่าและเปิดใช้งาน ID</span>
                </>
              )}
            </button>
          </form>
        ) : (
          /* 4. Login & Register Forms */
          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                อีเมลผู้ใช้งาน <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-pink-200/60 focus:border-pink-500 focus:outline-none text-sm bg-pink-50/10 transition-colors"
                />
                <Mail className="absolute left-3.5 top-3 w-4 h-4 text-[#DB2777]" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-slate-700">
                  รหัสผ่านบัญชี {mode === 'register' && '(อย่างน้อย 6 ตัวอักษร)'} <span className="text-rose-500">*</span>
                </label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot-password');
                      setError('');
                      setSuccessMessage('');
                    }}
                    className="text-[11px] text-[#DB2777] hover:underline font-medium cursor-pointer"
                  >
                    ลืมรหัสผ่าน?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type="password"
                  required
                  minLength={6}
                  placeholder={mode === 'register' ? 'กำหนดรหัสผ่าน 6 ตัวอักษรขึ้นไป' : 'ป้อนรหัสผ่านของคุณ'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-pink-200/60 focus:border-pink-500 focus:outline-none text-sm bg-pink-50/10 transition-colors"
                />
                <Lock className="absolute left-3.5 top-3 w-4 h-4 text-[#DB2777]" />
              </div>
            </div>

            {/* Quick Link for Forgot PIN */}
            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot-pin');
                    setError('');
                    setSuccessMessage('');
                  }}
                  className="text-[11px] text-slate-500 hover:text-[#DB2777] inline-flex items-center gap-1 font-medium cursor-pointer"
                >
                  <Key className="w-3 h-3 text-[#DB2777]" />
                  <span>ลืมรหัส PIN 6 หลัก? ส่งลิงก์ตั้งค่าทางอีเมล</span>
                </button>
              </div>
            )}

            {/* Action Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white rounded-xl font-bold text-sm shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-2 active:scale-98 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <span>กำลังดำเนินการ...</span>
              ) : (
                <>
                  <span>{mode === 'login' ? 'เข้าสู่ระบบ' : 'สมัครสมาชิก (สร้างรหัสผ่าน)'}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Google SSO Divider */}
            <div className="relative flex py-2 items-center">
              <div className="flex-grow border-t border-slate-200"></div>
              <span className="flex-shrink mx-4 text-slate-400 text-xs">หรือ</span>
              <div className="flex-grow border-t border-slate-200"></div>
            </div>

            {/* Google Single Sign-On Button */}
            <button
              type="button"
              onClick={handleGoogleSSO}
              disabled={loading}
              className="w-full py-2.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2.5 shadow-2xs active:scale-98 cursor-pointer disabled:opacity-50"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-4 h-4"
              />
              <span>เข้าสู่ระบบด้วย Google SSO</span>
            </button>

            {/* Switch Mode */}
            <div className="text-center text-xs mt-4 pt-2 border-t border-slate-100">
              {mode === 'login' ? (
                <p className="text-slate-600">
                  ยังไม่มีบัญชีสมาชิก?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('register');
                      setError('');
                      setIsDomainError(false);
                      setIsUserNotFound(false);
                    }}
                    className="text-[#2A59FE] font-bold hover:underline cursor-pointer"
                  >
                    สมัครสมาชิกที่นี่
                  </button>
                </p>
              ) : (
                <p className="text-slate-600">
                  มีบัญชีอยู่แล้ว?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setError('');
                      setIsDomainError(false);
                      setIsUserNotFound(false);
                    }}
                    className="text-[#2A59FE] font-bold hover:underline cursor-pointer"
                  >
                    เข้าสู่ระบบที่นี่
                  </button>
                </p>
              )}
            </div>

            {/* Quick Policies & About Links for Mobile Visitors */}
            <div className="mt-4 pt-3 border-t border-slate-100/80">
              <div className="text-[11px] font-bold text-slate-400 mb-2 text-center uppercase tracking-wider">
                ข้อมูลและนโยบายร้านค้า
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11.5px] text-slate-600 font-medium">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.history.pushState({}, '', '/aboutus');
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-[#2A59FE] hover:underline cursor-pointer"
                >
                  เกี่ยวกับเรา
                </button>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.history.pushState({}, '', '/member-privilege');
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-rose-600 hover:text-rose-700 font-bold hover:underline cursor-pointer"
                >
                  สมัครกิฟฟารีน 180.-
                </button>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.history.pushState({}, '', '/privacy-policy');
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-[#2A59FE] hover:underline cursor-pointer"
                >
                  นโยบายส่วนบุคคล
                </button>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.history.pushState({}, '', '/pdpa');
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-emerald-700 hover:text-emerald-800 font-semibold hover:underline cursor-pointer"
                >
                  PDPA
                </button>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.history.pushState({}, '', '/shipping-policy');
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-[#2A59FE] hover:underline cursor-pointer"
                >
                  จัดส่ง & ประกัน 7 วัน
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
